import {dateFormat} from 'common/dateFormatterConfig';
import {AGREEMENT_TYPE_MAP} from 'models/agreements';
import moment from 'moment';
import React from 'react';
import {Button, Spinner} from 'react-bootstrap';
import { CHARGING_AGREEMENT_STATUS_MAP, CHARGING_BILLING_CYCLE_MAP, ChargingAgreement, ChargingAgreementStatus, ChargingBillingCycle, ChargingPurchaseOptionType, ChargingThirdPartyCompensationOptionType } from 'models/charging/agreement';
import { ChargingStationList } from './ChargingStation/ChargingStationList';
import { isNotNullOrUndefined } from 'common/data-util';
import { AgreementInfoColumn, FieldSize } from './AgreementInfoColumn';
import { MeteringPointList } from './MeteringPoints/MeteringPointList';
import { ConditionalComponentWrapper } from 'components/common/ConditionalComponentWrapper';
import { CustomerPermission, useAuthContext } from 'services/context/authentication.context';

interface ChargingAgreementCardProps {
    agreement: ChargingAgreement;
    getFilePreview: () => void;
    isFilePreviewLoading: boolean;
    openOffer: () => void;
}

export default function ChargingAgreementCard({ agreement, getFilePreview, isFilePreviewLoading, openOffer }: ChargingAgreementCardProps) {
    const { hasPermission } = useAuthContext();
    
    const getAgreementStatusClassName = (status : ChargingAgreementStatus) => {
        switch (status){
            case ChargingAgreementStatus.ACTIVE:
                return 'active-status';
            case ChargingAgreementStatus.ENDED:
                return 'ended-status';
            case ChargingAgreementStatus.PENDING:
                return 'pending-status';
            case ChargingAgreementStatus.REJECTED:
                return 'rejected-status';
            default:
                return 'active-status';
        }
    }

    const ActionButtons = (
        <div className="d-flex flex-column flex-lg-row w-100 mx-0">
            { agreement.status !== ChargingAgreementStatus.PENDING ? (
                <div className="px-lg-2">
                    <Button 
                        variant="outline-primary" 
                        size="lg" 
                        block 
                        className="d-flex align-items-center justify-content-center text-nowrap" 
                        disabled={isFilePreviewLoading} 
                        onClick={getFilePreview}
                    >
                        {   isFilePreviewLoading &&
                            <Spinner animation="border" className="mr-2" />
                        }
                        Vaata lepingut
                    </Button>
                </div>
                ) : null 
            }
            <div className="px-lg-2 mt-2 mt-lg-0">
                { agreement.status === ChargingAgreementStatus.PENDING && hasPermission(CustomerPermission.WEB_ADMIN) ? (
                    <Button variant="outline-primary" size="lg" className="text-nowrap" block onClick={openOffer}>Vaata pakkumist</Button> 
                ) : null
                }
            </div>
        </div>
    );

    return (
      <div className="d-flex flex-column bg-white rounded mt-1 mb-2 px-3 px-lg-4 py-5">
          <div className={`d-flex justify-content-between align-items-center pb-2 w-100`}>
              <div className="d-flex">
                  <i className={`icon__charging d-inline-block mr-2 mt-1`}></i>
                  <div className="d-flex flex-column">
                      <div className="d-flex flex-column flex-lg-row align-items-lg-center">
                          <h2 className="u-fs-24 font-weight-bold mb-0">
                              {AGREEMENT_TYPE_MAP["CHARGING"]} nr { agreement.contractNumber }
                          </h2>
                          <div className="electricity_agreements__status text-nowrap ml-lg-3 mt-lg-2 mt-4">
                              <span className={`${getAgreementStatusClassName(agreement.status)}`}>{CHARGING_AGREEMENT_STATUS_MAP[agreement.status]}</span>
                          </div>
                      </div>
                      <div className="u-fs-20 mt-2 d-flex flex-column">
                        <strong>{ agreement.serviceOption.name }</strong>
                        <small>{ agreement.serviceOption.description }</small>
                     </div>
                  </div>
              </div>
              <div className="d-flex align-items-center">
                  <div className="d-none d-lg-flex">
                      { ActionButtons }
                  </div>
              </div>
          </div>
          <CardDetailedInfo
            agreement={agreement}
            actionButtons={ActionButtons}
          />
      </div>
    );
}

interface CardDetailedInfoProps {
    agreement: ChargingAgreement;
    actionButtons: JSX.Element;
}

function CardDetailedInfo({ agreement, actionButtons}: CardDetailedInfoProps) {
    const pricing = agreement.pricing;

    const showCardSection = isNotNullOrUndefined(agreement.simCardPaymentType) || isNotNullOrUndefined(agreement.simCardCount) || isNotNullOrUndefined(pricing.simCardMonthlyFeeEuros)
        || isNotNullOrUndefined(agreement.rfidPaymentCardCount) || isNotNullOrUndefined(pricing.rfidPaymentCardMonthlyFeeEuros);

    const showInfraSection = isNotNullOrUndefined(pricing.infrastructureUsageMonthlyFeePerNozzleEuros) || isNotNullOrUndefined(pricing.infrastructureUsageOneTimeFeePerNozzleEuros)
        || isNotNullOrUndefined(pricing.mediationAndBillingMonthlyFeePerNozzleEuros);

    const showBasicRent = agreement.purchaseOption.type === ChargingPurchaseOptionType.RENT && 
        isNotNullOrUndefined(pricing.devicesRentalMonthlyFeeEuros);
    const showFullServiceRent = agreement.purchaseOption.type === ChargingPurchaseOptionType.FULL_SERVICE_RENT && 
        isNotNullOrUndefined(pricing.devicesFullServiceRentalMonthlyFeeEuros);

    const showServiceSectionVatLabel = showCardSection || showInfraSection || showBasicRent || showFullServiceRent || 
        isNotNullOrUndefined(pricing.thirdPartyChargingMarginalCentsKwh) || isNotNullOrUndefined(pricing.chargingServiceMonthlyFeeEuros) || isNotNullOrUndefined(pricing.devicesCostEuros);
        
    const showDeviceSectionVatLabel = isNotNullOrUndefined(pricing.installationFeeEuros) || isNotNullOrUndefined(pricing.maintenanceMonthlyFeeEuros);
    const showNetoCompensation = agreement.thirdPartyCompensationOption?.type === ChargingThirdPartyCompensationOptionType.NETO && isNotNullOrUndefined(pricing.thirdPartyCompensationNetoFeeEuros);

    return (
      <>
          <div className="d-flex flex-column">
            <div className="electricity-agreement-card-detailed-info__table">
                  <div className="d-flex flex-column flex-lg-row ml-3 mb-2">
                      <AgreementInfoColumn
                          title="Lepingu algus"
                          value={agreement.startDate ? moment(agreement.startDate).format(dateFormat) : '-'}
                          titleFieldSize={FieldSize.LG}
                      />
                      <AgreementInfoColumn
                          title="Lepingu lõpp"
                          value={agreement.endDate ? moment(agreement.endDate).format(dateFormat) : agreement.startDate ? 'Tähtajatu' : '-'}
                          titleFieldSize={FieldSize.LG}
                      />
                  </div>
                  <div className='d-flex flex-column flex-lg-row ml-3'>
                    <>
                        <ConditionalComponentWrapper condition={isNotNullOrUndefined(pricing.thirdPartyChargingMarginalCentsKwh)}>
                            <AgreementInfoColumn
                                title="Teenuse marginaal kolmandate isikute tasuliste laadimiste eest:"
                                value={`${pricing.thirdPartyChargingMarginalCentsKwh} s/kWh`}
                                titleFieldSize={FieldSize.LG}
                            />
                        </ConditionalComponentWrapper>
                        <ConditionalComponentWrapper condition={isNotNullOrUndefined(pricing.chargingServiceMonthlyFeeEuros)}>
                            <AgreementInfoColumn
                                title="Teenuse kuutasu:"
                                value={`${pricing.chargingServiceMonthlyFeeEuros} €`}
                                titleFieldSize={FieldSize.LG}
                            />
                        </ConditionalComponentWrapper>
                    </>
                  </div>
                  <ConditionalComponentWrapper condition={showCardSection} className='mt-2'>
                    <ConditionalComponentWrapper condition={isNotNullOrUndefined(agreement.simCardPaymentType)} className='d-flex flex-column flex-lg-row ml-3'>
                        <AgreementInfoColumn
                            title="SIM-kaardi makse sagedus:"
                            value={CHARGING_BILLING_CYCLE_MAP[agreement.simCardPaymentType! as ChargingBillingCycle]}
                            titleFieldSize={FieldSize.LG}
                        />
                    </ConditionalComponentWrapper>
                    <div className='d-flex flex-column flex-lg-row ml-3'>
                        <>
                            <ConditionalComponentWrapper condition={isNotNullOrUndefined(agreement.simCardCount)}>
                                <AgreementInfoColumn
                                    title="SIM-kaartide arv:"
                                    value={`${agreement.simCardCount} tk`}
                                    titleFieldSize={FieldSize.LG}
                                />
                            </ConditionalComponentWrapper>
                            <ConditionalComponentWrapper condition={isNotNullOrUndefined(pricing.simCardMonthlyFeeEuros)}>
                                <AgreementInfoColumn
                                    title="SIM-kaardi kuutasu kaardi kohta:"
                                    value={`${pricing.simCardMonthlyFeeEuros} €`}
                                    titleFieldSize={FieldSize.LG}
                                />
                            </ConditionalComponentWrapper>
                        </>
                    </div>
                    <div className='d-flex flex-column flex-lg-row ml-3'>
                        <>
                            <ConditionalComponentWrapper condition={isNotNullOrUndefined(agreement.rfidPaymentCardCount)}>
                                <AgreementInfoColumn
                                    title="RFID-kaartide arv:"
                                    value={`${agreement.rfidPaymentCardCount} tk`}
                                    titleFieldSize={FieldSize.LG}
                                />
                            </ConditionalComponentWrapper>
                            <ConditionalComponentWrapper condition={isNotNullOrUndefined(pricing.rfidPaymentCardMonthlyFeeEuros)}>
                                <AgreementInfoColumn
                                    title="RFID-kaardi hind:"
                                    value={`${pricing.rfidPaymentCardMonthlyFeeEuros} €`}
                                    titleFieldSize={FieldSize.LG}
                                />
                            </ConditionalComponentWrapper>
                        </>
                    </div>
                  </ConditionalComponentWrapper>
                  <ConditionalComponentWrapper condition={showInfraSection} className='mt-2'>
                    <div className="d-flex flex-column flex-lg-row ml-3">
                        <ConditionalComponentWrapper condition={isNotNullOrUndefined(pricing.infrastructureUsageOneTimeFeePerNozzleEuros)}>
                            <AgreementInfoColumn
                                title="Infrastruktuuri kasutamise ühekordne tasu otsiku kohta:"
                                value={`${pricing.infrastructureUsageOneTimeFeePerNozzleEuros} €`}
                                titleFieldSize={FieldSize.LG}
                            />
                        </ConditionalComponentWrapper>
                        <ConditionalComponentWrapper condition={isNotNullOrUndefined(pricing.infrastructureUsageMonthlyFeePerNozzleEuros)}>
                            <AgreementInfoColumn
                                title="Infrastruktuuri kasutamise kuutasu laadija otsiku kohta:"
                                value={`${pricing.infrastructureUsageMonthlyFeePerNozzleEuros} €`}
                                titleFieldSize={FieldSize.LG}
                            />
                        </ConditionalComponentWrapper>
                    </div>
                    <div className="d-flex flex-column flex-lg-row ml-3">
                        <ConditionalComponentWrapper condition={isNotNullOrUndefined(pricing.mediationAndBillingMonthlyFeePerNozzleEuros)}>
                            <AgreementInfoColumn
                                title="Arveldusteenuse ja arveldamise vahendamisteenuse kuutasu:"
                                value={`${pricing.mediationAndBillingMonthlyFeePerNozzleEuros} €`}
                                titleFieldSize={FieldSize.LG}
                            />
                        </ConditionalComponentWrapper>
                    </div>
                  </ConditionalComponentWrapper>
            </div>
          </div>
         {
            showServiceSectionVatLabel && (
                <div className="row ml-3 mt-2">
                    <div className="col text-gray-700">
                        Lisandub käibemaks
                    </div>
                </div>
            )
         }
          <div className="u-hr"/>
          <div className="row ml-3 mb-2">
            <div className="col d-flex flex-column u-fs-20">
                <strong>{ agreement.purchaseOption.name }</strong>
                <small>{ agreement.purchaseOption.description }</small>
            </div>
          </div>
          <div className="electricity-agreement-card-detailed-info__table">
            <div className="d-flex flex-column flex-lg-row ml-3">
                <ConditionalComponentWrapper condition={isNotNullOrUndefined(pricing.installationFeeEuros)}>
                    <AgreementInfoColumn
                        title="Seadmete paigaldustasu:"
                        value={`${pricing.installationFeeEuros} €`}
                        titleFieldSize={FieldSize.LG}
                    />
                </ConditionalComponentWrapper>
                <ConditionalComponentWrapper condition={isNotNullOrUndefined(pricing.maintenanceMonthlyFeeEuros)}>
                    <AgreementInfoColumn
                        title="Seadmete hoolduse kuutasu:"
                        value={`${pricing.maintenanceMonthlyFeeEuros} €`}
                        titleFieldSize={FieldSize.LG}
                    />
                </ConditionalComponentWrapper>
            </div>
            <div className="d-flex flex-column flex-lg-row ml-3">
                <ConditionalComponentWrapper condition={showFullServiceRent}>
                    <AgreementInfoColumn
                        title="Seadmete täisteenusrendi kuutasu:"
                        value={`${pricing.devicesFullServiceRentalMonthlyFeeEuros} €`}
                        titleFieldSize={FieldSize.LG}
                    />
                </ConditionalComponentWrapper>
                <ConditionalComponentWrapper condition={showBasicRent}>
                    <AgreementInfoColumn
                        title="Seadmete rendi kuutasu:"
                        value={`${pricing.devicesRentalMonthlyFeeEuros} €`}
                        titleFieldSize={FieldSize.LG}
                    />
                </ConditionalComponentWrapper>
            </div>
            <div className="d-flex flex-column flex-lg-row ml-3">
                <ConditionalComponentWrapper condition={isNotNullOrUndefined(pricing.devicesCostEuros)}>
                    <AgreementInfoColumn
                        title="Seadmete maksumus:"
                        value={`${pricing.devicesCostEuros} €`}
                        titleFieldSize={FieldSize.LG}
                    />
                </ConditionalComponentWrapper>
            </div>
            <ConditionalComponentWrapper condition={isNotNullOrUndefined(agreement.address)} className="d-flex flex-column flex-lg-row ml-3">
                <AgreementInfoColumn
                    title="Kasutuskoha aadress:"
                    value={agreement.address}
                    fixedWidth={false}
                    titleFieldSize={FieldSize.LG}
                />
            </ConditionalComponentWrapper>
          </div>
          {
            showDeviceSectionVatLabel && (
                <div className="row ml-3 mt-2">
                    <div className="col text-gray-700">
                        Lisandub käibemaks
                    </div>
                </div>
            )
         }
          <div className="u-hr"/>
         {
            agreement.thirdPartyCompensationOption && (
                <>
                     <div className="row ml-3 mb-2">
                        <div className="col d-flex flex-column u-fs-20">
                            <strong>{ agreement.thirdPartyCompensationOption?.name }</strong>
                            <small>{ agreement.thirdPartyCompensationOption?.description }</small>
                        </div>
                    </div>
                    <div className="electricity-agreement-card-detailed-info__table">
                        <div className="d-flex flex-column flex-lg-row ml-3">
                            <ConditionalComponentWrapper condition={showNetoCompensation}>
                                <AgreementInfoColumn
                                    title="Neto kompensatsioon:"
                                    value={`${pricing.thirdPartyCompensationNetoFeeEuros} €`}
                                    titleFieldSize={FieldSize.LG}
                                />
                            </ConditionalComponentWrapper>
                        </div>
                    </div>
                    {
                        showNetoCompensation && (
                            <div className="row ml-3 mt-2">
                                <div className="col text-gray-700">
                                    Lisandub käibemaks
                                </div>
                            </div>
                        )
                    }
                </>
            )
         }
          <div className="d-flex d-lg-none px-2 px-lg-4 my-3">
              { actionButtons }
          </div>
          <MeteringPointList 
                meteringPoints={[agreement.meteringPoint]} withProductionCapacity={false} isOffer={false}            
          />
          <ChargingStationList
            stations={agreement.chargingStations}
          />
      </>
    )
}